/**
 * components/auth/LoginCard.tsx
 * Simple login card for username/password, with emergency reset to recovery admin.
 * Recovery behavior:
 *  - Do NOT auto-fill username/password after reset.
 *  - Do NOT display credentials anywhere in the confirmation.
 *  - Only show the reminder hint after reset.
 */

import React, { useState } from 'react'
import { useAuthStore } from '../../store/auth'
import { useUserStore } from '@/store/user'
import { DatabaseService } from '@/lib/databaseService'

/**
 * LoginCard
 * Prompts for credentials and logs in via auth store.
 * Emergency reset:
 *  - Resets users to a single recovery admin (phone-based credentials) silently.
 *  - Shows only the reminder hint; does not auto-fill or display credentials.
 */
export function LoginCard() {

  const userLogin = useUserStore((s) => s.login)
  const resetToDefaultAdmin = useAuthStore((s) => s.resetToDefaultAdmin)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setInfo(null)
    // const res2 = await DatabaseService.signIn(username,password)
    // console.log(res2); 
    const res2 = await userLogin(username,password)
    console.log(res2);
    if (!res2.ok) {
      setError(res2.reason || 'فشل تسجيل الدخول')
    } else {
      setError(null)
    }
  }

  /**
   * Emergency reset to a single recovery admin after confirmation.
   * - Do not show username/password in the confirmation.
   * - Do not auto-fill any fields after reset.
   * - Show only the reminder hint.
   */
  const onResetAdmin = () => {
    const ok = confirm(
      [
        'سيتم إعادة تعيين جميع المستخدمين إلى مستخدم مدير واحد فقط.',
        '',
        'سيتم عرض التلميح التالي بعد الاستعادة:',
        '• ماهو رقم جوالك؟ وماهو رقم الجوال المسجل في أبشر للسجل التجاري أبعاد؟',
        '',
        'هل تريد المتابعة؟',
      ].join('\n')
    )
    if (!ok) return
    resetToDefaultAdmin()
    setError(null)
    setInfo('تذكير: ماهو رقم جوالك؟ وماهو رقم الجوال المسجل في أبشر للسجل التجاري أبعاد؟')
  }

  return (
    <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
      <h1 className="mb-4 text-center text-xl font-bold text-slate-900">تسجيل الدخول</h1>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="mb-1 block text-sm text-slate-600">اسم المستخدم</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            placeholder="اسم المستخدم"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-slate-600">كلمة المرور</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            placeholder="••••••••"
          />
        </div>
        {error && <div className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700">{error}</div>}
        {info && <div className="rounded-lg bg-emerald-50 p-2 text-sm text-emerald-700">{info}</div>}
        <button
          type="submit"
          className="w-full rounded-lg bg-slate-900 px-4 py-2 font-medium text-white hover:bg-slate-800"
        >
          دخول
        </button>

        {/* Recovery link with reminder-only behavior */}
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={onResetAdmin}
            className="text-xs text-slate-600 underline underline-offset-2 hover:text-slate-800"
            title="استعادة بيانات المدير — تذكير: ماهو رقم جوالك؟ وماهو رقم الجوال المسجل في أبشر للسجل التجاري أبعاد؟"
          >
            نسيت بيانات المدير؟ استعادة
          </button>
        </div>
      </form>
    </div>
  )
}
